import {
  ConductCaseSeverity,
  ConductCaseCategory,
  ConductCaseStatus,
  ConductCaseResolutionType,
} from '@sandtech-hub-monorepo/ehub-client';

export const REGISTRAR_EMAIL = 'transcripts@frontiertech.edu';

export interface TranscriptClassEnrollment {
  letterGrade?: string | null;
  gradePointsValue?: number | null;
  status?: string | null;
  graduatedAt?: unknown;
  failedAt?: unknown;
  class: {
    term?: string | null;
    startDate?: string | null;
    name: string;
  };
}

export interface TranscriptCourseEnrollment {
  status: string;
  letterGrade?: string | null;
  gradePointsValue?: number | null;
  graduatedAt?: unknown;
  failedAt?: unknown;
  course: {
    name: string;
    abbreviation: string;
  };
  classEnrollments: TranscriptClassEnrollment[];
}

export interface TranscriptTermGPA {
  term: string;
  gpa?: number | null;
}

export interface TranscriptCourse {
  abbreviation: string;
  name: string;
  attemptedCredits: number;
  earnedCredits: number;
  grade: string | null;
  gradePoints: number | null;
}

export interface TranscriptConductCase {
  severity: ConductCaseSeverity;
  category: ConductCaseCategory;
  status: ConductCaseStatus;
  resolutionType?: ConductCaseResolutionType | null;
  term: string;
  className?: string | null;
}

export interface TranscriptTermConductCase {
  severity: ConductCaseSeverity;
  category: ConductCaseCategory;
  status: ConductCaseStatus;
  resolutionType?: ConductCaseResolutionType | null;
  className?: string | null;
}

export interface TranscriptTerm {
  term: string;
  courses: TranscriptCourse[];
  attemptedCredits: number;
  earnedCredits: number;
  gpa: number | null;
  /** Running cumulative GPA through this term, computed from grade points. */
  cumulativeGPA: number | null;
  academicStandingLabel: string;
  conductCases: TranscriptTermConductCase[];
}

export interface TranscriptSummary {
  totalAttemptedCredits: number;
  totalEarnedCredits: number;
  totalGradePoints: number;
  cumulativeGPA: number | null;
}

const SEVERITY_ORDER: Record<ConductCaseSeverity, number> = {
  [ConductCaseSeverity.ScholasticWarning]: 1,
  [ConductCaseSeverity.AcademicProbation]: 2,
  [ConductCaseSeverity.AcademicSuspension]: 3,
  [ConductCaseSeverity.AcademicDismissal]: 4,
};

const STANDING_LABELS: Record<ConductCaseSeverity, string> = {
  [ConductCaseSeverity.ScholasticWarning]: 'Scholastic Warning',
  [ConductCaseSeverity.AcademicProbation]: 'Academic Probation',
  [ConductCaseSeverity.AcademicSuspension]: 'Academic Suspension',
  [ConductCaseSeverity.AcademicDismissal]: 'Academic Dismissal',
};

const CATEGORY_LABELS: Record<ConductCaseCategory, string> = {
  [ConductCaseCategory.GpaBelowMinimum]: 'GPA Below Minimum',
  [ConductCaseCategory.AcademicIntegrityViolation]: 'Integrity Violation',
};

const STATUS_LABELS: Record<ConductCaseStatus, string> = {
  [ConductCaseStatus.Active]: 'Active',
  [ConductCaseStatus.UnderAppeal]: 'Under Appeal',
  [ConductCaseStatus.Closed]: 'Closed',
};

const RESOLUTION_LABELS: Record<ConductCaseResolutionType, string> = {
  [ConductCaseResolutionType.Dismissed]: 'Dismissed',
  [ConductCaseResolutionType.Exonerated]: 'Exonerated',
  [ConductCaseResolutionType.Remediated]: 'Remediated',
};

/**
 * Compute the academic standing label and visible conduct cases for a term.
 * Shows all standing levels including Good Standing and Warnings.
 * Excludes DISMISSED and EXONERATED cases (admin error / overturned).
 */
export const getSeverityLabel = (severity: ConductCaseSeverity): string =>
  STANDING_LABELS[severity];

export const getCategoryLabel = (category: ConductCaseCategory): string =>
  CATEGORY_LABELS[category];

export const getStatusLabel = (
  status: ConductCaseStatus,
  resolutionType?: ConductCaseResolutionType | null
): string => {
  if (status === ConductCaseStatus.Closed && resolutionType) {
    return `${STATUS_LABELS[status]} (${RESOLUTION_LABELS[resolutionType]})`;
  }
  return STATUS_LABELS[status];
};

export const getTermStandingInfo = (
  term: string,
  conductCases: TranscriptConductCase[]
): { label: string; cases: TranscriptTermConductCase[] } => {
  const visibleCases: TranscriptTermConductCase[] = [];
  let worstSeverity: ConductCaseSeverity | null = null;
  let worstOrder = 0;

  for (const c of conductCases) {
    if (c.term !== term) continue;
    if (
      c.resolutionType === ConductCaseResolutionType.Dismissed ||
      c.resolutionType === ConductCaseResolutionType.Exonerated
    )
      continue;

    visibleCases.push({
      severity: c.severity,
      category: c.category,
      status: c.status,
      resolutionType: c.resolutionType,
      className: c.className,
    });

    // Remediated cases are shown but don't affect the standing label
    if (c.resolutionType === ConductCaseResolutionType.Remediated) continue;

    const order = SEVERITY_ORDER[c.severity];
    if (order > worstOrder) {
      worstOrder = order;
      worstSeverity = c.severity;
    }
  }

  const label = worstSeverity
    ? STANDING_LABELS[worstSeverity]
    : 'Good Academic Standing';

  return { label, cases: visibleCases };
};

// 'IP' (in-progress) joins the withdrawal/incomplete codes as GPA-neutral: it
// must never contribute grade points even if a stray gradePointsValue exists.
const GPA_EXCLUDED_GRADES = ['W', 'WP', 'I', 'IP'];

const getCourseGrade = (
  courseEnrollment: TranscriptCourseEnrollment
): string | null => {
  if (courseEnrollment.letterGrade) return courseEnrollment.letterGrade;

  const graduated = courseEnrollment.classEnrollments.find(
    (ce) => ce.graduatedAt
  );
  if (graduated?.letterGrade) return graduated.letterGrade;

  const latest =
    courseEnrollment.classEnrollments[
      courseEnrollment.classEnrollments.length - 1
    ];
  return latest?.letterGrade ?? null;
};

const getCourseGradePointsValue = (
  courseEnrollment: TranscriptCourseEnrollment
): number | null => {
  if (courseEnrollment.gradePointsValue != null)
    return courseEnrollment.gradePointsValue;

  const graduated = courseEnrollment.classEnrollments.find(
    (ce) => ce.graduatedAt
  );
  if (graduated?.gradePointsValue != null) return graduated.gradePointsValue;

  const latest =
    courseEnrollment.classEnrollments[
      courseEnrollment.classEnrollments.length - 1
    ];
  return latest?.gradePointsValue ?? null;
};

const isCourseCompleted = (
  courseEnrollment: TranscriptCourseEnrollment
): boolean => {
  return courseEnrollment.status === 'GRADUATED';
};

export const buildTranscriptCourse = (
  courseEnrollment: TranscriptCourseEnrollment,
  credits: number
): TranscriptCourse => {
  const grade = getCourseGrade(courseEnrollment);
  const completed = isCourseCompleted(courseEnrollment);
  const earnedCredits = completed ? credits : 0;
  const gpaValue = getCourseGradePointsValue(courseEnrollment);

  // GPA-excluded grades don't contribute grade points
  const isExcluded =
    grade !== null && GPA_EXCLUDED_GRADES.includes(grade);
  const gradePoints =
    gpaValue !== null && !isExcluded ? credits * gpaValue : null;

  return {
    abbreviation: courseEnrollment.course.abbreviation,
    name: courseEnrollment.course.name,
    attemptedCredits: credits,
    earnedCredits,
    grade,
    gradePoints,
  };
};

/**
 * Build a TranscriptCourse from a specific class enrollment.
 * Used when showing individual class attempts per term (retakes show as separate rows).
 */
export const buildTranscriptCourseFromClass = (
  courseEnrollment: TranscriptCourseEnrollment,
  classEnrollment: TranscriptClassEnrollment,
  credits: number
): TranscriptCourse => {
  // An ongoing, still-ungraded attempt shows as 'IP' (In-Progress) rather than a
  // blank '—' — matches the code documented in the official transcript legend.
  const grade =
    classEnrollment.letterGrade ??
    (classEnrollment.status === 'ONGOING' ? 'IP' : null);
  const completed = !!classEnrollment.graduatedAt;
  const earnedCredits = completed ? credits : 0;
  const gpaValue = classEnrollment.gradePointsValue ?? null;

  const isExcluded =
    grade !== null && GPA_EXCLUDED_GRADES.includes(grade);
  const gradePoints =
    gpaValue !== null && !isExcluded ? credits * gpaValue : null;

  return {
    abbreviation: courseEnrollment.course.abbreviation,
    name: courseEnrollment.course.name,
    attemptedCredits: credits,
    earnedCredits,
    grade,
    gradePoints,
  };
};

/**
 * Collects transfer / prior-credit courses: graduated course enrollments that
 * have no class attempt (e.g. ALX courses equivalated into the program). They
 * are shown with grade 'T', count toward earned credits, but are GPA-neutral
 * (no attempted credits and no grade points), so they live in their own section.
 */
export const collectTransferCourses = (
  courseEnrollments: TranscriptCourseEnrollment[],
  creditsByCourseDef: Map<string, number>
): TranscriptCourse[] => {
  const result: TranscriptCourse[] = [];
  for (const ce of courseEnrollments) {
    if (ce.classEnrollments.length > 0) continue;
    const completed = ce.status === 'GRADUATED' || !!ce.graduatedAt;
    if (!completed) continue;
    const credits = creditsByCourseDef.get(ce.course.abbreviation) ?? 0;
    result.push({
      abbreviation: ce.course.abbreviation,
      name: ce.course.name,
      attemptedCredits: 0,
      earnedCredits: credits,
      grade: ce.letterGrade ?? 'T',
      gradePoints: null,
    });
  }
  return result;
};

/**
 * Groups course enrollments by the academic term of their class enrollments.
 * Each class enrollment appears as a separate row in its respective term,
 * so retaken courses show up in both their original and retake terms.
 * Terms are sorted by the earliest class startDate within each term.
 */
export const groupCoursesByTerm = (
  courseEnrollments: TranscriptCourseEnrollment[],
  creditsByCourseDef: Map<string, number>,
  termGPAs?: TranscriptTermGPA[],
  conductCases?: TranscriptConductCase[]
): TranscriptTerm[] => {
  const termEntries = new Map<
    string,
    {
      courseEnrollment: TranscriptCourseEnrollment;
      classEnrollment: TranscriptClassEnrollment;
    }[]
  >();
  const termEarliestDate = new Map<string, string>();

  for (const courseEnrollment of courseEnrollments) {
    for (const classEnrollment of courseEnrollment.classEnrollments) {
      const term = classEnrollment.class.term;
      if (!term) continue;

      const existing = termEntries.get(term) ?? [];
      existing.push({ courseEnrollment, classEnrollment });
      termEntries.set(term, existing);

      if (
        classEnrollment.class.startDate &&
        (!termEarliestDate.has(term) ||
          classEnrollment.class.startDate < termEarliestDate.get(term)!)
      ) {
        termEarliestDate.set(term, classEnrollment.class.startDate);
      }
    }
  }

  const termGPAMap = new Map(
    termGPAs?.map((t) => [t.term, t.gpa ?? null]) ?? []
  );

  const sortedTerms = [...termEntries.entries()].sort((a, b) => {
    const dateA = termEarliestDate.get(a[0]) ?? '';
    const dateB = termEarliestDate.get(b[0]) ?? '';
    return dateA.localeCompare(dateB);
  });

  const terms: TranscriptTerm[] = [];

  // Running totals used to compute the cumulative GPA through each term.
  let runningGradePoints = 0;
  let runningGradedCredits = 0;

  for (const [term, entries] of sortedTerms) {
    entries.sort((a, b) => {
      const dateA = a.classEnrollment.class.startDate ?? '';
      const dateB = b.classEnrollment.class.startDate ?? '';
      return dateA.localeCompare(dateB);
    });

    const transcriptCourses = entries.map((entry) => {
      const credits =
        creditsByCourseDef.get(entry.courseEnrollment.course.abbreviation) ?? 0;
      return buildTranscriptCourseFromClass(
        entry.courseEnrollment,
        entry.classEnrollment,
        credits
      );
    });

    const attemptedCredits = transcriptCourses.reduce(
      (sum, c) => sum + c.attemptedCredits,
      0
    );
    const earnedCredits = transcriptCourses.reduce(
      (sum, c) => sum + c.earnedCredits,
      0
    );

    // Grade points and the credits that count toward GPA for this term.
    const gradedCourses = transcriptCourses.filter(
      (c) => c.gradePoints != null
    );
    const termGradePoints = gradedCourses.reduce(
      (sum, c) => sum + (c.gradePoints ?? 0),
      0
    );
    const termGradedCredits = gradedCourses.reduce(
      (sum, c) => sum + c.attemptedCredits,
      0
    );

    // Prefer the backend-provided term GPA; otherwise derive it from grade points.
    const backendGpa = termGPAMap.get(term);
    let gpa: number | null;
    if (backendGpa !== undefined) {
      gpa = backendGpa;
    } else {
      gpa = termGradedCredits > 0 ? termGradePoints / termGradedCredits : null;
    }

    // Accumulate running totals to derive the cumulative GPA through this term.
    runningGradePoints += termGradePoints;
    runningGradedCredits += termGradedCredits;
    const cumulativeGPA =
      runningGradedCredits > 0
        ? runningGradePoints / runningGradedCredits
        : null;

    terms.push({
      term,
      courses: transcriptCourses,
      attemptedCredits,
      earnedCredits,
      gpa,
      cumulativeGPA,
      ...(() => {
        const standingInfo = getTermStandingInfo(term, conductCases ?? []);
        return {
          academicStandingLabel: standingInfo.label,
          conductCases: standingInfo.cases,
        };
      })(),
    });
  }

  return terms;
};

export const computeSummary = (
  terms: TranscriptTerm[],
  cumulativeGPA: number | null | undefined
): TranscriptSummary => {
  const totalAttemptedCredits = terms.reduce(
    (sum, t) => sum + t.attemptedCredits,
    0
  );
  const totalEarnedCredits = terms.reduce(
    (sum, t) => sum + t.earnedCredits,
    0
  );
  const totalGradePoints = terms.reduce(
    (sum, t) =>
      sum + t.courses.reduce((s, c) => s + (c.gradePoints ?? 0), 0),
    0
  );

  return {
    totalAttemptedCredits,
    totalEarnedCredits,
    totalGradePoints,
    cumulativeGPA: cumulativeGPA ?? null,
  };
};
